import {
  TRADE_ARROW,
  TRADE_ARROWS_FONT_FAMILY,
} from "../../../positionLines/tradeArrowsFont";
import { roundedRectPath } from "../../text/roundedRect";
import {
  POSITION_CARD_BORDER_WIDTH_PX,
  POSITION_CLOSE_ICON_LINE_WIDTH_PX,
} from "./positionConstants";

// Inset of the hover / pressed background of the close cell inside the card
const CLOSE_HIGHLIGHT_INSET_PX = 2;
const HALF = 0.5;

const pnlColorName = (pnl) => {
  if (pnl > 0) return "positionProfit";
  if (pnl < 0) return "positionLoss";

  return "positionPnlNeutral";
};

/**
 * Widths of the card cells: direction, leverage, PnL, close (E10: no Flip).
 *
 * @param {{ card: { leverageText: string, pnlText: string } }} position
 * @param {{ measure: Function, font: string, sizes: object }} params
 * @returns {{ cells: number[], width: number }}
 */
export const layoutCard = ({ card }, { measure, font, sizes }) => {
  const padding = 2 * sizes.cellPaddingX;
  const cells = [
    sizes.iconCellWidth,
    Math.max(measure(card.leverageText, font) + padding, sizes.leverageCellMinWidth),
    Math.max(measure(card.pnlText, font) + padding, sizes.pnlCellMinWidth),
    sizes.closeCellWidth,
  ];

  return { cells, width: cells.reduce((sum, cell) => sum + cell, 0) };
};

// Thin vertical lines after the direction cell and before the close cell, on
// whole pixels; leverage and PnL share one cell, as on moon.com
const drawSeparators = (context, rect, [iconCell, , , closeCell]) => {
  context.beginPath();
  [rect.x + iconCell, rect.x + rect.width - closeCell].forEach((x) => {
    context.moveTo(Math.round(x) + HALF, rect.y);
    context.lineTo(Math.round(x) + HALF, rect.y + rect.height);
  });
  context.stroke();
};

const drawCloseCell = (context, rect, { sizes, colors, highlight }) => {
  const centreX = rect.x + rect.width / 2;
  const centreY = rect.y + rect.height / 2;
  const half = sizes.closeIconSize / 2;

  if (highlight) {
    roundedRectPath(
      context,
      {
        x: rect.x + CLOSE_HIGHLIGHT_INSET_PX,
        y: rect.y + CLOSE_HIGHLIGHT_INSET_PX,
        width: rect.width - 2 * CLOSE_HIGHLIGHT_INSET_PX,
        height: rect.height - 2 * CLOSE_HIGHLIGHT_INSET_PX,
      },
      sizes.cardRadius,
    );
    context.fillStyle = colors[highlight.isPressed ? "positionClosePressedBg" : "positionCloseHoverBg"].css;
    context.fill();
  }

  context.beginPath();
  context.moveTo(centreX - half, centreY - half);
  context.lineTo(centreX + half, centreY + half);
  context.moveTo(centreX + half, centreY - half);
  context.lineTo(centreX - half, centreY + half);
  context.lineWidth = POSITION_CLOSE_ICON_LINE_WIDTH_PX;
  context.lineCap = "round";
  context.strokeStyle = colors[highlight ? "positionCloseIconHover" : "positionCloseIcon"].css;
  context.stroke();
};

/**
 * Card of a position on its entry line (moon-position-lines-spec.md): it
 * darkens from left to right in three steps, as on moon.com — a double
 * chevron of the direction on the lightest cell, the leverage, the live
 * PnL in the colour of its sign and the close button; a light border and
 * thin lines around the direction and close cells, as on moon.com.
 *
 * @param {object} textLayer
 * @param {{ rect: object, cells: number[], position: object }} card - rect in CSS px
 * @param {object} style
 * @param {string} style.font
 * @param {object} style.sizes - from positionSizesFor
 * @param {object} style.colors - theme colours
 * @param {{ isPressed: boolean } | null} style.highlight - of the close button
 * @returns {{ x: number, y: number, width: number, height: number }} rect of the close button
 */
export const drawCard = (textLayer, { rect, cells, position }, { font, sizes, colors, highlight }) => {
  const { context } = textLayer;
  const [iconCell, leverageCell, pnlCell, closeCell] = cells;
  const centreY = rect.y + rect.height / 2;
  const closeX = rect.x + rect.width - closeCell;
  const { pnl, pnlText, leverageText } = position.card;
  // Half-pixel inset keeps the 1 px border sharp
  const outline = { x: rect.x + HALF, y: rect.y + HALF, width: rect.width - 1, height: rect.height - 1 };

  context.save();
  roundedRectPath(context, outline, sizes.cardRadius);
  context.fillStyle = colors.positionCardBg.css;
  context.fill();
  context.clip();
  context.fillStyle = colors.positionCardIconBg.css;
  context.fillRect(rect.x, rect.y, iconCell, rect.height);
  context.fillStyle = colors.positionCardCloseBg.css;
  context.fillRect(closeX, rect.y, closeCell, rect.height);
  context.restore();

  context.lineWidth = POSITION_CARD_BORDER_WIDTH_PX;
  context.strokeStyle = colors.positionCardBorder.css;
  drawSeparators(context, rect, cells);
  roundedRectPath(context, outline, sizes.cardRadius);
  context.stroke();

  // The glyph is centred in its em box, so the middle baseline centres it
  context.font = `${sizes.iconSize}px "${TRADE_ARROWS_FONT_FAMILY}"`;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillStyle = colors[position.isLong ? "positionProfit" : "positionLoss"].css;
  context.fillText(position.isLong ? TRADE_ARROW.up : TRADE_ARROW.down, rect.x + iconCell / 2, centreY);

  textLayer.drawText(leverageText, rect.x + iconCell + sizes.cellPaddingX, centreY, {
    font,
    color: colors.positionCardText.css,
    align: "left",
  });
  textLayer.drawText(pnlText, rect.x + iconCell + leverageCell + pnlCell - sizes.cellPaddingX, centreY, {
    font,
    color: colors[pnlColorName(pnl)].css,
    align: "right",
  });

  const closeRect = { x: closeX, y: rect.y, width: closeCell, height: rect.height };

  drawCloseCell(context, closeRect, { sizes, colors, highlight });

  return closeRect;
};
